import React, { useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

function currentMonthIso() {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Phase J.2 Pass 2 — Bulk "Generate this month's invoices" modal on
 * /admin/invoices. Picks a billing month, calls
 * POST /admin/invoices/generate-monthly, and renders a result summary
 * with deep-links to each created invoice.
 */
export default function GenerateMonthlyInvoicesModal({ open, onOpenChange, onCreated }) {
    const [month, setMonth] = useState(currentMonthIso());
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
            toast.error("Month must be YYYY-MM");
            return;
        }
        setSubmitting(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/invoices/generate-monthly", { billing_month: month });
            setResult(data);
            const n = (data.created || []).length;
            toast.success(`Generated ${n} invoice${n === 1 ? "" : "s"} for ${month}`);
            if (onCreated) onCreated(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const close = () => {
        if (submitting) return;
        setResult(null);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
            <DialogContent className="sm:max-w-lg" data-testid="generate-monthly-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles size={16} className="text-blue-900" />
                        Generate monthly invoices
                    </DialogTitle>
                    <DialogDescription>
                        Creates one draft invoice per active monthly rental for the chosen month.
                        Re-running on the same month is safe — already-invoiced rentals are skipped.
                    </DialogDescription>
                </DialogHeader>

                {!result && (
                    <div className="space-y-3 py-2">
                        <div>
                            <Label htmlFor="billing-month">Billing month</Label>
                            <Input
                                id="billing-month"
                                type="month"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                data-testid="generate-monthly-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                Format: YYYY-MM. Defaults to the current calendar month.
                            </p>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="space-y-4 py-2" data-testid="generate-monthly-result">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2">
                            <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0" />
                            <div className="text-sm">
                                <p className="font-semibold text-emerald-900">
                                    {(result.created || []).length} invoice{(result.created || []).length === 1 ? "" : "s"} created
                                </p>
                                <p className="text-emerald-800 text-xs">
                                    {(result.skipped || []).length} skipped (already invoiced) ·
                                    {" "}{(result.errors || []).length} error{(result.errors || []).length === 1 ? "" : "s"}
                                </p>
                            </div>
                        </div>

                        {(result.created || []).length > 0 && (
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Created drafts</p>
                                <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="generate-monthly-created-list">
                                    {result.created.map((id) => (
                                        <li key={id}>
                                            <Link
                                                to={`/admin/invoices/${id}`}
                                                className="text-sm text-blue-900 hover:underline font-mono"
                                                data-testid={`generate-monthly-link-${id}`}
                                            >
                                                {id.slice(0, 8)}…
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {(result.errors || []).length > 0 && (
                            <div>
                                <p className="text-xs uppercase tracking-wider text-red-700 mb-2 flex items-center gap-1">
                                    <AlertTriangle size={12} /> Errors
                                </p>
                                <ul className="space-y-1 max-h-40 overflow-y-auto">
                                    {result.errors.map((e, i) => (
                                        <li key={i} className="text-xs text-red-700 font-mono">
                                            {e.rental_id?.slice(0, 8)}… — {e.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {!result && (
                        <>
                            <Button variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
                            <Button onClick={submit} disabled={submitting}
                                    className="bg-blue-900 hover:bg-blue-800 gap-2"
                                    data-testid="generate-monthly-submit">
                                {submitting ? "Generating…" : <>Generate for {month}</>}
                            </Button>
                        </>
                    )}
                    {result && (
                        <Button onClick={close} className="bg-blue-900 hover:bg-blue-800" data-testid="generate-monthly-close">Done</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
